import { Box, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnlineTourCard } from '../components/OnlineTourCard';
import OnlineTourDetailHotels from '../components/OnlineTourDetailHotels';
import OnlineTourDetailPresentations from '../components/OnlineTourDetailPresentations';
import OnlineTourDetailPrices from '../components/OnlineTourDetailPrices';
import OnlineTourDetailTransportation from '../components/OnlineTourDetailTransportation';
import {
  IOnlineTourSearchResult,
  OnlineTourDetailTabTypes,
  TourParameter,
} from '../utils/types';
import '../App.css';
import OnlineTourDetailTransportationMobile from '../components/OnlineTourDetailTransportationMobile';
import { getTourParam } from '../utils';

type OnlineTourDetailPageProps = {
  data: IOnlineTourSearchResult;
  tourId: number;
};

export default function OnlineTourDetailContainer({
  data,
  tourId,
}: OnlineTourDetailPageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [hideTab, setHideTab] = useState(false);
  const [activeTab, setActiveTab] =
    useState<OnlineTourDetailTabTypes>('presentation');
  const handleTabHide = () => {
    setHideTab(true);
    setActiveTab('prices');
  };

  return (
    <Box sx={{ maxWidth: '1400px', margin: 'auto' }}>
      <Box className="print-section">
        <Box className="tab-section">
          <OnlineTourCard
            disableActions
            tourId={data.id}
            coverImg={
              data.images.length > 0
                ? data.images.find((img) => img.favorite)?.path ??
                  data.images[0].path
                : null
            }
            tourImage={data.images.length > 0 ? data.images : null}
            flightCompanyInfo={
              data?.flightCompanyInfo && data?.flightCompanyInfo[0]
            }
            tourName={data.name}
            tourCode={data.code}
            start={{
              departureCity: data.departureDepartureCity,
              departureAirport: data.departureDeparture,
              arrivalCity: data.departureArrivalCity,
              arrivalAirport: data.departureArrival,
            }}
            end={{
              departureCity: data.arrivalDepartureCity,
              departureAirport: data.arrivalDeparture,
              arrivalCity: data.arrivalArrivalCity,
              arrivalAirport: data.arrivalArrival,
            }}
            tourStartDate={data.departureDate}
            tourFinishDate={data.arrivalDate}
            night={data.dayCount - 1}
            day={data.dayCount}
            tourPrice={data.adultPrice}
            currencyCode={data.currencyCode}
            remainingTime={data.remainingDay}
            remainingSeat={data.availableCapacity}
            whatsappPhoneNumber={getTourParam(data, 'WHATSAPP_PHONE_NUMBER')}
            showBookButton={
              getTourParam(data, 'SALES_PERMISSION') === '1' ||
              getTourParam(data, 'RESERVATION_PERMISSION') === '1'
            }
            showCallMeButton={getTourParam(data, 'CONTACT_ME') === '1'}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Tabs
          variant="fullWidth"
          onChange={(_, value) => setActiveTab(value)}
          value={activeTab}
          sx={{ width: 600, maxWidth: '100%', mx: 'auto' }}
        >
          {!hideTab && (
            <Tab
              label={t('onlineTourDetail.presentation')}
              value="presentation"
            />
          )}
          <Tab label={t('onlineTourDetail.prices')} value="prices" />
          <Tab label={t('onlineTourDetail.hotels')} value="hotels" />
          <Tab
            label={t('onlineTourDetail.transportation')}
            value="transportation"
          />
        </Tabs>

        <Box className="tab-section" sx={{ mt: 2 }}>
          <Box
            className="tab-section"
            sx={{
              border: '1px groove',
              borderRadius: '10px',
              margin: '20px 0px',
              padding: 2,
              mt: 2,
            }}
          >
            {activeTab === 'presentation' && !hideTab && (
              <OnlineTourDetailPresentations
                tourId={tourId}
                onEmptyDetails={(hasData) => {
                  if (!hasData) {
                    handleTabHide();
                  }
                }}
              />
            )}
            {activeTab === 'prices' && (
              <OnlineTourDetailPrices tourId={tourId} />
            )}
            {activeTab === 'hotels' && (
              <OnlineTourDetailHotels tourId={tourId} />
            )}
            {activeTab === 'transportation' &&
              (isMobile ? (
                <OnlineTourDetailTransportationMobile tourId={tourId} />
              ) : (
                <OnlineTourDetailTransportation tourId={tourId} />
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
